import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import BuyerPanelAlerts from '../common/BuyerPanelAlerts';
import MediatorPanelAlerts from '../common/MediatorPanelAlerts';
import { useAuth } from '../../context/AuthContext';

const Layout = ({
  title,
  showBack = false,
  backTo = -1,
  onBackClick,
  actions = [],
  breadcrumbs = [],
  sidebarVariant = 'default',
  className = '',
  children
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const { hasRole } = useAuth();

  const isAdmin = hasRole('admin');
  const isBuyer = hasRole('buyer');
  const isMediator = hasRole('mediator');

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: 'var(--bg)',
      display: 'flex',
      flexDirection: 'row'
    }}>
      {/* Sidebar drawer — must sit above page content (filters, tables) on mobile */}
      <div
        className={`layout-sidebar-drawer${sidebarOpen && !isDesktop ? ' is-open' : ''}${isDesktop ? ' is-desktop' : ''}`}
        style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '256px',
        height: '100vh',
        zIndex: isDesktop ? 40 : (sidebarOpen ? 100 : -1),
        transform: isDesktop ? 'translateX(0)' : (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)'),
        visibility: !isDesktop && !sidebarOpen ? 'hidden' : 'visible',
        pointerEvents: !isDesktop && !sidebarOpen ? 'none' : 'auto',
        transition: 'transform 0.3s ease',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e2e8f0',
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
      }}>
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          variant={sidebarVariant}
          isDesktop={isDesktop}
        />
      </div>

      {/* Mobile backdrop */}
      {sidebarOpen && !isDesktop && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 90
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div
        className="layout-shell"
        style={{
        flex: 1,
        marginLeft: isDesktop ? '256px' : '0',
        width: isDesktop ? 'calc(100% - 256px)' : '100%',
        minHeight: '100vh',
        transition: 'margin-left 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        overflow: isDesktop ? 'hidden' : 'auto',
        overflowX: isDesktop ? 'hidden' : 'hidden',
        maxWidth: '100%',
      }}>
        {/* Header */}
        <div
          className="layout-header-bar"
          style={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          padding: isDesktop ? '16px 24px' : '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 20,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <Header
            title={title}
            showBack={showBack}
            backTo={backTo}
            onBackClick={onBackClick}
            actions={[
              ...actions,
              !isDesktop && {
                icon: 'menu',
                variant: 'secondary',
                size: 'sm',
                onClick: () => setSidebarOpen(true),
                className: 'p-2',
              }
            ].filter(Boolean)}
            breadcrumbs={breadcrumbs}
          />
        </div>

        {/* Page content */}
        <main
          className="layout-main-content"
          style={{
          flex: 1,
          padding: '0',
          backgroundColor: '#f8fafc',
          overflow: isDesktop ? 'hidden' : 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          maxWidth: '100%',
        }}>
          {isBuyer && <BuyerPanelAlerts />}
          {isMediator && <MediatorPanelAlerts />}
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default Layout;
